import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import PageShell from "@/components/PageShell";
import LaunchActivityCard from "./LaunchActivityCard";
import {
  Compass, PackagePlus, TrendingUp, Gauge, ShieldCheck, History,
  Eye, EyeOff, ChevronUp, ChevronDown, Sparkles, FlaskConical,
  Activity, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import { devWarn } from "@/lib/devlog";

const ALL_WIDGETS = [
  { id: "scan", label: "Opportunity scanner summary" },
  { id: "launch_activity", label: "Launch activity · 30-day rollup" },
  { id: "subscription_pulse", label: "Subscription pulse · live MRR" },
  { id: "performance", label: "Active program performance" },
  { id: "alerts", label: "Vendor alerts" },
  { id: "leakage", label: "Competitive leakage insights" },
  { id: "revenue", label: "Recurring revenue snapshot" },
  { id: "cohorts", label: "Cohort trends" },
  { id: "workflows", label: "Recent workflow statuses" },
];

const STORAGE_KEY = "nodera-intelligence-layout-v2";

const STATUS_TINT = {
  paid: "bg-emerald-100 text-emerald-800",
  payment_pending: "bg-amber-100 text-amber-800",
  invoice_submitted: "bg-blue-100 text-blue-800",
  completed: "bg-slate-100 text-slate-700",
  fulfillment_in_progress: "bg-indigo-100 text-indigo-700",
  vendor_accepted: "bg-violet-100 text-violet-700",
  pending_vendor_acceptance: "bg-rose-50 text-rose-700",
};

function loadLayout() {
  let stored = null;
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(v) && v.length) stored = v;
  } catch (e) {
    // localStorage may be disabled (private mode) or contain invalid JSON.
    devWarn("[PortalIntelligence] loadLayout fallback:", e);
  }
  if (!stored) {
    return ALL_WIDGETS.map((w) => ({ id: w.id, hidden: false }));
  }
  // Merge in any widget IDs that were added to ALL_WIDGETS after the
  // user's layout was persisted, so new features surface automatically
  // instead of requiring a "reset layout" step.
  const known = new Set(stored.map((w) => w.id));
  const missing = ALL_WIDGETS
    .filter((w) => !known.has(w.id))
    .map((w) => ({ id: w.id, hidden: false }));
  if (missing.length === 0) return stored;
  return [...stored, ...missing];
}

export default function PortalIntelligence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState(loadLayout());
  const [editing, setEditing] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/clinic/intelligence")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  // Soft refresh in the background every 30s so MRR pulse feels alive.
  const refresh = () => {
    api.get("/clinic/intelligence")
      .then((r) => setData(r.data))
      .catch(() => {/* silent */});
  };
  useEffect(() => {
    load();
    const tick = () => { if (document.visibilityState === "visible") refresh(); };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  const persist = (next) => {
    setLayout(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      // Storage may be disabled or quota exceeded — non-fatal.
      devWarn("[PortalIntelligence] persist layout failed:", e);
    }
  };
  const toggle = (id) => persist(layout.map((w) => (w.id === id ? { ...w, hidden: !w.hidden } : w)));
  const move = (id, dir) => {
    const i = layout.findIndex((w) => w.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= layout.length) return;
    const next = [...layout];
    [next[i], next[j]] = [next[j], next[i]];
    persist(next);
  };

  const seedSample = async () => {
    setSeeding(true);
    try { await api.post("/clinic/sample-data/seed"); toast.success("Sample data seeded."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSeeding(false); }
  };

  return (
    <PageShell
      eyebrow="Clinic Intelligence Center"
      title="Your clinic at a glance."
      intro="Operational intelligence across opportunity, programs, vendors, market signals, and engagement. Show, hide, and reorder widgets to match how your team works."
      testId="intelligence-home"
      actions={
        <div className="flex items-center gap-2">
          {data && !data.has_sample_data && data.performance.summary.total_workflows === 0 && (
            <button onClick={seedSample} disabled={seeding} data-testid="home-seed-sample" className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60">
              <FlaskConical className="w-3.5 h-3.5" /> {seeding ? "Seeding…" : "Seed sample data"}
            </button>
          )}
          <button
            onClick={() => setEditing((v) => !v)}
            data-testid="home-customize"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
          >
            {editing ? "Done" : "Customize layout"}
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="text-sm text-slate-500">Loading intelligence…</div>
      ) : !data ? null : (
        <>
          {data.has_sample_data && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 mb-5 text-[11px] text-amber-900 flex gap-2">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 text-amber-700" />
              <div>
                <b>Sample data active.</b> The widgets below include sample workflows clearly marked “Sample · ” —
                clear them in <Link to="/portal/analytics/programs" className="underline font-semibold">Program Performance</Link> when ready.
              </div>
            </div>
          )}

          {layout.map((w) => {
            const meta = ALL_WIDGETS.find((x) => x.id === w.id);
            if (!meta) return null;
            return (
              <Widget
                key={w.id}
                id={w.id}
                label={meta.label}
                hidden={w.hidden}
                editing={editing}
                onToggle={() => toggle(w.id)}
                onUp={() => move(w.id, -1)}
                onDown={() => move(w.id, 1)}
              >
                {!w.hidden && renderWidget(w.id, data)}
              </Widget>
            );
          })}
        </>
      )}
    </PageShell>
  );
}

function Widget({ id, label, hidden, editing, onToggle, onUp, onDown, children }) {
  return (
    <section className="mb-5" data-testid={`widget-${id}`}>
      {editing && (
        <div className="flex items-center gap-2 mb-1.5 text-[11px] text-slate-500">
          <span className="font-semibold uppercase tracking-wide">{label}</span>
          <button onClick={onUp} className="p-1 hover:bg-slate-100 rounded" data-testid={`widget-up-${id}`}><ChevronUp className="w-3.5 h-3.5" /></button>
          <button onClick={onDown} className="p-1 hover:bg-slate-100 rounded" data-testid={`widget-down-${id}`}><ChevronDown className="w-3.5 h-3.5" /></button>
          <button onClick={onToggle} className="p-1 hover:bg-slate-100 rounded ml-auto" data-testid={`widget-toggle-${id}`}>
            {hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}
      {!hidden && children}
    </section>
  );
}

function renderWidget(id, data) {
  if (id === "scan") return <ScanCard scan={data.scan_summary} />;
  if (id === "launch_activity") return <LaunchActivityCard />;
  if (id === "subscription_pulse") return <SubscriptionPulseCard pulse={data.subscription_pulse} />;
  if (id === "performance") return <PerformanceCard perf={data.performance} programs={data.programs} />;
  if (id === "alerts") return <AlertsCard alerts={data.vendor_alerts} />;
  if (id === "leakage") return <LeakageCard rows={data.leakage_top} />;
  if (id === "revenue") return <RevenueCard perf={data.performance} />;
  if (id === "cohorts") return <CohortCard c={data.cohort_snapshot} />;
  if (id === "workflows") return <RecentWorkflows rows={data.recent_workflows} />;
  return null;
}

function ScanCard({ scan }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const buildFromCat = async (cat) => {
    setBusy(cat);
    try {
      const r = await api.post(`/clinic/programs/from-category/${cat}`);
      toast.success(`Draft created · "${r.data.name}"`);
      nav(`/portal/programs/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  if (!scan?.exists) {
    return (
      <CardLink to="/portal/opportunity-scanner" tone="blue" testId="scan-empty">
        <div className="flex items-center gap-3">
          <Compass className="w-6 h-6" />
          <div>
            <div className="text-sm font-semibold">Run your first opportunity scan</div>
            <div className="text-xs opacity-90">Identify the top revenue categories for your clinic.</div>
          </div>
        </div>
      </CardLink>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Opportunity scanner</div>
        <Link to="/portal/opportunity-scanner" className="text-[11px] text-blue-700 font-semibold hover:underline">Open scanner →</Link>
      </div>
      <div className="flex items-center gap-4">
        <div className="font-display text-4xl font-extrabold text-slate-900">{scan.overall_readiness_score}<span className="text-base text-slate-400">/100</span></div>
        <div className="text-xs text-slate-500">Operational readiness</div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {(scan.top_categories || []).map((c) => (
          <div key={c.category} className="rounded-xl bg-slate-50 p-3 border border-slate-100 flex flex-col" data-testid={`scan-top-${c.category}`}>
            <div className="text-[10px] uppercase font-semibold text-slate-500 truncate">{c.label}</div>
            <div className="font-bold text-slate-900 text-xl mt-1">{c.score}</div>
            <button
              onClick={() => buildFromCat(c.category)}
              disabled={busy === c.category}
              data-testid={`home-build-${c.category}`}
              className="mt-2 inline-flex items-center justify-center gap-1 bg-slate-900 text-white rounded-full px-2 py-1 text-[10px] font-semibold hover:bg-slate-800 disabled:opacity-60"
            >
              <PackagePlus className="w-2.5 h-2.5" />
              {busy === c.category ? "Drafting…" : "Build"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PerformanceCard({ perf, programs }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Active program performance</div>
          <Link to="/portal/analytics/programs" className="text-[11px] text-blue-700 font-semibold hover:underline">Open dashboard →</Link>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Workflows" v={perf.summary.total_workflows} />
          <Kpi label="Fulfillment" v={`${perf.summary.fulfillment_rate}%`} />
          <Kpi label="Total revenue" v={`$${perf.summary.total_revenue.toLocaleString()}`} />
          <Kpi label="Recurring revenue" v={`$${perf.summary.recurring_revenue_estimate.toLocaleString()}`} />
        </div>
        {perf.timeline.length > 0 && (
          <ResponsiveContainer width="100%" height={120} className="mt-3">
            <AreaChart data={perf.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip />
              <Area type="monotone" dataKey="new" stroke="#2563eb" fill="#2563eb" fillOpacity={0.18} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Active programs</div>
          <Link to="/portal/programs" className="text-[11px] text-blue-700 font-semibold hover:underline">Open builder →</Link>
        </div>
        {programs.active.length === 0 ? (
          <div className="text-xs text-slate-500 py-3">No active programs yet. <Link to="/portal/programs" className="text-blue-700 underline">Build one</Link> from a template.</div>
        ) : (
          <ul className="space-y-2">
            {programs.active.map((p) => (
              <li key={p.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2" data-testid={`home-active-${p.id}`}>
                <div>
                  <div className="font-semibold text-slate-900">{p.name}</div>
                  <div className="text-[10px] text-slate-500 capitalize">{p.category} · {p.cadence?.replace("_", " ")} · ${p.price || 0}</div>
                </div>
                <Link to={`/portal/programs/${p.id}`} className="text-[11px] text-blue-700 font-semibold hover:underline">Edit →</Link>
              </li>
            ))}
          </ul>
        )}
        <div className="text-[10px] text-slate-500 mt-2">{programs.draft_count} drafts · {programs.total} total</div>
      </div>
    </div>
  );
}

function AlertsCard({ alerts }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Vendor alerts</div>
        <Link to="/portal/analytics/vendors" className="text-[11px] text-blue-700 font-semibold hover:underline">Open scoring →</Link>
      </div>
      {(!alerts || alerts.length === 0) ? (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> No vendor reliability concerns detected.
        </div>
      ) : (
        <ul className="space-y-2">
          {alerts.map((a) => (
            <li key={a.vendor_id} className="flex items-center justify-between text-sm border border-rose-100 bg-rose-50 rounded-lg px-3 py-2" data-testid={`alert-${a.vendor_id}`}>
              <span className="font-semibold text-rose-900">{a.vendor_name}</span>
              <span className="text-[10px] uppercase font-bold text-rose-700">Reliability {a.reliability_score}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LeakageCard({ rows }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Competitive leakage insights</div>
        <Link to="/portal/analytics/leakage" className="text-[11px] text-blue-700 font-semibold hover:underline">Open dashboard →</Link>
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        {(rows || []).map((r) => (
          <div key={r.category} className="rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid={`leak-${r.category}`}>
            <div className="text-[10px] uppercase font-semibold text-slate-500 truncate">{r.label}</div>
            <div className="text-[18px] font-extrabold text-slate-900 mt-1">+{r.yoy_growth_pct}%</div>
            <div className="text-[10px] text-slate-500 capitalize">YoY · {r.leakage_risk} leakage</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenueCard({ perf }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-100">Recurring revenue snapshot</div>
      <div className="font-display text-4xl font-extrabold mt-1">${perf.summary.recurring_revenue_estimate.toLocaleString()}</div>
      <div className="text-xs text-emerald-100 mt-1">Estimated recurring share of paid invoices · {perf.summary.paid_invoices} paid</div>
    </div>
  );
}

function CohortCard({ c }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Cohort trends</div>
        <Link to="/portal/analytics/cohorts" className="text-[11px] text-blue-700 font-semibold hover:underline">Open analytics →</Link>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Kpi label="Consumers" v={c.total_consumers} />
        <Kpi label="30-day" v={`${c.avg_retention_30}%`} />
        <Kpi label="60-day" v={`${c.avg_retention_60}%`} />
        <Kpi label="90-day" v={`${c.avg_retention_90}%`} />
      </div>
    </div>
  );
}

function RecentWorkflows({ rows }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Recent workflow statuses</div>
        <Link to="/portal/workflows" className="text-[11px] text-blue-700 font-semibold hover:underline">Open queue →</Link>
      </div>
      {(!rows || rows.length === 0) ? (
        <div className="text-xs text-slate-500 py-3">No workflows yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((w) => (
            <li key={w.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5" data-testid={`recent-wf-${w.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{w.order_id} · {w.service_summary}</div>
                <div className="text-[10px] text-slate-500 truncate">{w.vendor_name} · {w.category}</div>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase font-bold ${STATUS_TINT[w.status] || "bg-slate-100 text-slate-700"}`}>
                {w.status?.replace(/_/g, " ")}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CardLink({ to, tone, testId, children }) {
  const cls = tone === "blue"
    ? "bg-gradient-to-br from-blue-600 to-indigo-600 text-white"
    : "bg-white border border-slate-200";
  return (
    <Link to={to} data-testid={testId} className={`block rounded-2xl ${cls} p-5 hover:shadow-md transition`}>
      {children}
    </Link>
  );
}

function SubscriptionPulseCard({ pulse }) {
  const targetMrr = pulse?.mrr || 0;
  const [displayMrr, setDisplayMrr] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const from = displayMrr;
    const to = targetMrr;
    const dur = 900;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayMrr(from + (to - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMrr]);

  if (!pulse?.has_data) {
    return (
      <CardLink to="/portal/order-services" tone="blue" testId="pulse-empty">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6" />
          <div>
            <div className="text-sm font-semibold">Activate your first cash-pay subscription</div>
            <div className="text-xs opacity-90">Enroll a consumer in a program to start your live MRR pulse.</div>
          </div>
        </div>
      </CardLink>
    );
  }

  const wow = pulse.wow_delta_pct || 0;
  const wowTrend = wow > 0 ? "up" : wow < 0 ? "down" : "flat";

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-900/10" data-testid="subscription-pulse-card">
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white px-5 pt-5 pb-4">
        <div className="absolute top-5 right-5 flex items-center gap-1.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-200">Live</span>
        </div>

        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-200/90 flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5" /> Subscription pulse
        </div>

        <div className="flex items-end gap-3 mt-2">
          <div className="font-display text-5xl font-extrabold tabular-nums" data-testid="pulse-mrr">
            ${Math.round(displayMrr).toLocaleString()}
          </div>
          <div className="text-xs text-emerald-100/80 pb-2">MRR</div>
        </div>
        <div className="text-[11px] text-emerald-100/80 mt-0.5">
          ${pulse.arr.toLocaleString()} ARR · {pulse.recurring_count} recurring · {pulse.active_count} active
        </div>

        {pulse.sparkline?.length > 0 && (
          <div className="mt-3 h-16 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={pulse.sparkline} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="pulseFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34d399" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 10, fontSize: 11, color: "#fff" }}
                  formatter={(v) => [`${v} new`, "Activations"]}
                  labelStyle={{ color: "#a7f3d0" }}
                />
                <Area type="monotone" dataKey="new" stroke="#34d399" strokeWidth={2} fill="url(#pulseFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100 bg-white border-t border-slate-100">
        <PulseStat
          label="New this week"
          value={`+${pulse.new_this_week}`}
          sub={
            <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${
              wowTrend === "up" ? "text-emerald-700" : wowTrend === "down" ? "text-rose-700" : "text-slate-500"
            }`}>
              {wowTrend === "up" ? <ArrowUpRight className="w-3 h-3" /> :
               wowTrend === "down" ? <ArrowDownRight className="w-3 h-3" /> :
               <Minus className="w-3 h-3" />}
              {Math.abs(wow)}% WoW
            </span>
          }
          testId="pulse-new-week"
        />
        <PulseStat
          label="Net new"
          value={`${pulse.net_new_this_week >= 0 ? "+" : ""}${pulse.net_new_this_week}`}
          sub={`${pulse.churned_this_week} churn`}
          testId="pulse-net-new"
        />
        <PulseStat
          label="Billing in 7d"
          value={pulse.upcoming_billings_7d}
          sub="cycles due"
          testId="pulse-billing-7d"
        />
        <PulseStat
          label="Recurring"
          value={pulse.recurring_count}
          sub={`of ${pulse.active_count} active`}
          testId="pulse-recurring"
        />
      </div>

      {pulse.by_category?.length > 0 && (
        <div className="bg-white border-t border-slate-100 px-5 py-3">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2 flex items-center justify-between">
            <span>MRR by category</span>
            <Link to="/portal/order-services" className="text-[10px] text-blue-700 font-semibold hover:underline normal-case tracking-normal" data-testid="pulse-open-orders">
              Open services →
            </Link>
          </div>
          <div className="space-y-1.5">
            {pulse.by_category.map((c) => {
              const total = pulse.mrr || 1;
              const pct = Math.max(2, Math.round((c.mrr / total) * 100));
              const atRisk = c.risk === "at_risk";
              return (
                <div key={c.category} className="flex items-center gap-2 text-xs" data-testid={`pulse-cat-${c.category}`}>
                  <div className="w-28 text-slate-700 truncate capitalize flex items-center gap-1">
                    {c.label}
                    {atRisk && (
                      <span
                        title={`${c.churn_7d} cancelled · ${c.new_7d} new this week`}
                        data-testid={`pulse-risk-${c.category}`}
                        className="inline-flex items-center gap-0.5 rounded-full bg-rose-50 border border-rose-200 px-1.5 py-px text-[9px] font-bold text-rose-700 uppercase tracking-wider"
                      >
                        <ArrowDownRight className="w-2.5 h-2.5" /> Risk
                      </span>
                    )}
                  </div>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${
                        atRisk ? "from-rose-500 to-rose-400" : "from-emerald-500 to-emerald-400"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="w-20 text-right tabular-nums font-semibold text-slate-900">
                    ${Math.round(c.mrr).toLocaleString()}
                  </div>
                  <div className="w-10 text-right text-[10px] text-slate-500 tabular-nums">{c.active}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PulseStat({ label, value, sub, testId }) {
  return (
    <div className="px-4 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
      <div className="font-display text-xl font-extrabold text-slate-900 tabular-nums mt-0.5">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function Kpi({ label, v }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
      <div className="text-[10px] uppercase font-semibold text-slate-500 truncate">{label}</div>
      <div className="font-bold text-xl text-slate-900 mt-1">{v}</div>
    </div>
  );
}

void Gauge; void History; void TrendingUp; void PackagePlus;
